import { Box, Tooltip } from '@mui/material';
import { ConfirmationDialog, PoolFormattedMessage } from '@synergy/components';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { PoolLink } from '@synergy/components-next';
import { ReactElement, useState } from 'react';
import { getFileSize, sortByProperty } from '@synergy/utils';

import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';

/** What every document list needs, whoever the document belongs to. */
export interface DocumentRecord {
    id: number;
    originalName?: string;
    title?: string;
    name?: string;
    pathname?: string;
    mimeType?: string;
    size?: number;
    /** Typed as a date in the models, delivered as an ISO string by the API. */
    createdAt?: string | Date;
    uploadProgress?: number;
}

export interface DocumentsTableProps<T extends DocumentRecord> {
    documents: T[];
    onEditTitle: (document: T) => void;
    onDownload: (url: string, documentName: string) => void;
    onDelete: (document: T) => void;
    /**
     * Where the file is reached: from the title, as on a supplier, or from the
     * row menu, as on a customer and a contact.
     */
    downloadFrom?: 'title' | 'menu';
    /** Columns this mask adds, e.g. the supplier's two visibility marks. */
    extraColumns?: PaloColumn<T>[];
}

/** A file type without its family: "application/pdf" reads as "pdf". */
const fileType = (mimeType: string): string => {
    const index = mimeType.indexOf('/');

    return index < 0 ? mimeType : mimeType.substring(index + 1);
};

/**
 * The documents of a customer, a supplier or a contact.
 *
 * The three masks held three copies of this table that differed in the name of
 * their document type and in nothing else, apart from the two marks and the
 * download link the supplier needs.
 */
export const DocumentsTable = <T extends DocumentRecord>(props: DocumentsTableProps<T>): ReactElement => {
    const [documentToDelete, setDocumentToDelete] = useState<T | null>(null);
    /**
     * Newest paper first. Sorted by title, a contract signed last week sat
     * somewhere in the middle of the alphabet and had to be hunted for (#1469).
     */
    const [sort, setSort] = useState<{ key: string; order: 'asc' | 'desc' }>({ key: 'createdAt', order: 'desc' });
    const locale = useAppSelector(bestLocaleSelector);

    const download = (document: T): void => props.onDownload(document.pathname ?? '', document.originalName ?? '');

    /**
     * The stored filename and the byte count say nothing about the paper: what
     * matters is which contract it is and whether it still holds. Both stay one
     * click away in the column chooser (#2193).
     */
    const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>(['name', 'size']);

    const columns: PaloColumn<T>[] = [
        {
            key: 'title',
            type: 'name',
            sortKey: 'title',
            head: <FormattedMessage id="app.label.title" />,
            cell: document =>
                'title' === props.downloadFrom ? (
                    <Tooltip title={<FormattedMessage id="app.product.detail.files.download" />} placement="top">
                        <Box>
                            <PoolLink colorVariant="primary" onClick={() => download(document)}>
                                {document.title}
                            </PoolLink>
                        </Box>
                    </Tooltip>
                ) : (
                    document.title
                ),
        },
        ...(props.extraColumns ?? []),
        {
            key: 'name',
            type: 'name',
            sortKey: 'name',
            head: <FormattedMessage id="app.label.filename" />,
            cell: document => document.name,
        },
        {
            key: 'size',
            type: 'fileSize',
            sortKey: 'size',
            head: <FormattedMessage id="app.label.filesize" />,
            cell: document => getFileSize(document.size, locale),
        },
        {
            key: 'mimeType',
            type: 'fileType',
            head: <FormattedMessage id="app.label.filetype" />,
            cell: document => fileType(document.mimeType ?? ''),
        },
        {
            key: 'createdAt',
            type: 'date',
            sortKey: 'createdAt',
            head: <FormattedMessage id="app.label.fileUploadedAt" />,
            cell: document =>
                document.createdAt ? (
                    <FormattedDate value={document.createdAt} year="numeric" month="short" day="2-digit" />
                ) : (
                    '–'
                ),
        },
    ];

    const rowActions = (document: T): PaloRowAction[] => [
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.documents.editTitle" />,
            onClick: () => props.onEditTitle(document),
        },
        ...('title' === props.downloadFrom
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.contactsAndOrganizations.documents.download" />,
                      onClick: () => download(document),
                  },
              ]),
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.documents.delete" />,
            onClick: () => setDocumentToDelete(document),
        },
    ];

    // A file still on its way up has no size and no type to show yet.
    const finished = sortByProperty({
        data: props.documents.filter(document => undefined === document.uploadProgress),
        // A date compares as a date, not as whatever its text happens to be.
        ...('createdAt' === sort.key
            ? { valueSelector: (document: T) => new Date(document.createdAt ?? 0).getTime() }
            : { key: sort.key as keyof T }),
        sortDirection: sort.order,
    });

    const handleDeleteConfirm = (): void => {
        if (documentToDelete) {
            props.onDelete(documentToDelete);
        }
        setDocumentToDelete(null);
    };

    return (
        <>
            <PaloListView<T, number>
                embedded={true}
                columns={columns}
                hiddenColumnKeys={hiddenColumnKeys}
                onToggleColumn={key =>
                    setHiddenColumnKeys(current =>
                        current.includes(key) ? current.filter(entry => entry !== key) : [...current, key]
                    )
                }
                items={finished}
                getRowId={document => document.id}
                emptyLabel={<FormattedMessage id="app.contactsAndOrganizations.documents.files" />}
                sort={sort}
                onSortChange={(key, order) => setSort({ key, order })}
                rowActions={rowActions}
            />

            <ConfirmationDialog
                isVisible={!!documentToDelete}
                title={<FormattedMessage id="app.contactsAndOrganizations.documents.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setDocumentToDelete(null)}
                onConfirm={handleDeleteConfirm}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.documents.deleteWarning"
                    values={{ name: documentToDelete?.title ?? documentToDelete?.name ?? '' }}
                />
            </ConfirmationDialog>
        </>
    );
};
